import React, { useEffect, useState } from 'react';
import Topbar from '../components/Topbar';
import { Save, Bell, Eye, Filter } from 'lucide-react';
import './SettingsPage.css';

interface Settings {
  usuario: {
    nome: string;
    email: string;
    telefone: string;
    funcao: string;
  };
  notificacoes: {
    votacoes: boolean;
    movimentos: boolean;
    novasProposicoes: boolean;
    pareceres: boolean;
    atualizacoesTemas: boolean;
  };
  visualizacao: {
    itensPerPage: number;
    tema: 'light' | 'dark';
    densiidade: 'compacto' | 'normal' | 'espaçoso';
  };
  filtrosDefault: {
    status: string[];
    tipos: string[];
  };
}

export default function SettingsPage() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [changed, setChanged] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch('/api/settings');
        const data = await res.json();
        setSettings(data);
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSave = () => {
    setChanged(false);
    alert('Configurações salvas com sucesso!');
  };

  if (loading || !settings) {
    return (
      <div className="settings-container">
        <Topbar title="Configurações" />
        <div className="settings-content">
          <div className="settings-loading">Carregando...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <Topbar title="Configurações" />
      <div className="settings-content">
        <div className="settings-header">
          <h2>Configurações</h2>
          {changed && (
            <button className="btn" onClick={handleSave}>
              <Save size={16} />
              Salvar Mudanças
            </button>
          )}
        </div>

        <div className="settings-grid">
          {/* Perfil */}
          <div className="settings-section">
            <h3>Perfil</h3>
            <div className="setting-group">
              <label>Nome</label>
              <input
                type="text"
                defaultValue={settings.usuario.nome}
                onChange={() => setChanged(true)}
              />
            </div>
            <div className="setting-group">
              <label>Email</label>
              <input
                type="email"
                defaultValue={settings.usuario.email}
                onChange={() => setChanged(true)}
              />
            </div>
            <div className="setting-group">
              <label>Telefone</label>
              <input
                type="tel"
                defaultValue={settings.usuario.telefone}
                onChange={() => setChanged(true)}
              />
            </div>
            <div className="setting-group">
              <label>Função</label>
              <input
                type="text"
                defaultValue={settings.usuario.funcao}
                onChange={() => setChanged(true)}
              />
            </div>
          </div>

          {/* Notificações */}
          <div className="settings-section">
            <div className="settings-section-header">
              <Bell size={20} />
              <h3>Notificações</h3>
            </div>
            <div className="setting-toggle">
              <label>Votações</label>
              <input
                type="checkbox"
                defaultChecked={settings.notificacoes.votacoes}
                onChange={() => setChanged(true)}
              />
            </div>
            <div className="setting-toggle">
              <label>Movimentos</label>
              <input
                type="checkbox"
                defaultChecked={settings.notificacoes.movimentos}
                onChange={() => setChanged(true)}
              />
            </div>
            <div className="setting-toggle">
              <label>Novas Proposições</label>
              <input
                type="checkbox"
                defaultChecked={settings.notificacoes.novasProposicoes}
                onChange={() => setChanged(true)}
              />
            </div>
            <div className="setting-toggle">
              <label>Pareceres</label>
              <input
                type="checkbox"
                defaultChecked={settings.notificacoes.pareceres}
                onChange={() => setChanged(true)}
              />
            </div>
            <div className="setting-toggle">
              <label>Atualizações de Temas</label>
              <input
                type="checkbox"
                defaultChecked={settings.notificacoes.atualizacoesTemas}
                onChange={() => setChanged(true)}
              />
            </div>
          </div>

          {/* Visualização */}
          <div className="settings-section">
            <div className="settings-section-header">
              <Eye size={20} />
              <h3>Visualização</h3>
            </div>
            <div className="setting-group">
              <label>Itens por página</label>
              <select
                defaultValue={settings.visualizacao.itensPerPage}
                onChange={() => setChanged(true)}
              >
                <option>10</option>
                <option>20</option>
                <option>50</option>
              </select>
            </div>
            <div className="setting-group">
              <label>Tema</label>
              <select
                defaultValue={settings.visualizacao.tema}
                onChange={() => setChanged(true)}
              >
                <option value="light">Claro</option>
                <option value="dark">Escuro</option>
              </select>
            </div>
            <div className="setting-group">
              <label>Densidade</label>
              <select
                defaultValue={settings.visualizacao.densiidade}
                onChange={() => setChanged(true)}
              >
                <option value="compacto">Compacto</option>
                <option value="normal">Normal</option>
                <option value="espaçoso">Espaçoso</option>
              </select>
            </div>
          </div>

          {/* Filtros Padrão */}
          <div className="settings-section">
            <div className="settings-section-header">
              <Filter size={20} />
              <h3>Filtros Padrão</h3>
            </div>
            <div className="setting-group">
              <label>Status</label>
              <div className="checkbox-group">
                {['tramitando', 'urgente', 'aprovado'].map((s) => (
                  <label key={s} className="checkbox-item">
                    <input
                      type="checkbox"
                      defaultChecked={settings.filtrosDefault.status.includes(
                        s
                      )}
                      onChange={() => setChanged(true)}
                    />
                    {s}
                  </label>
                ))}
              </div>
            </div>
            <div className="setting-group">
              <label>Tipos</label>
              <div className="checkbox-group">
                {['PL', 'PEC', 'PLP', 'MPV'].map((t) => (
                  <label key={t} className="checkbox-item">
                    <input
                      type="checkbox"
                      defaultChecked={settings.filtrosDefault.tipos.includes(
                        t
                      )}
                      onChange={() => setChanged(true)}
                    />
                    {t}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
